import { TableSortBy } from '@acceso/design-system';
import { debounce } from 'lodash';
import { create } from 'zustand';

import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { PaginationValues } from '@/hooks/usePagination';
import { endOfAppDay, startOfAppDay } from '@/utils/spain-time';
import { resetToFirstPage } from '@/utils/table-filters';

// The Emisora filter options are derived from this range, so an empty range
// would leave the dropdown holding only its placeholder while the grid lists
// every broadcast. The range is initialised and restored, never emptied.
//
// "Today" is the Madrid day, whatever the viewer's timezone (RCOMM-6264).
export function getDefaultParrillaDateRange(): { start: Date; end: Date } {
  return {
    start: startOfAppDay(),
    // Milliseconds included so this matches the range the date picker's "Hoy"
    // preset produces: otherwise the two ways of asking for today differ by
    // under a second and cost a second request and cache entry.
    end: endOfAppDay(),
  };
}

type ParrillaFiltersState = {
  order_by: string;
  review_state: number[];
  priorities: number[];
  gte: string;
  lte: string;
  channel_id: string;
  deliveryDate: string[];
  channel_media_type: ChannelMediaTypeEnum | '';
  program_source_id__isnull: boolean | null;
  program_viewed_place_id: string;
  paginationValues: PaginationValues;
  setFilters: (newFilters: Partial<ParrillaFiltersState>) => void;
  setPage: (page: number) => void;
  setRows: (rowsPerPage: number) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: TableSortBy) => void;
  clearFilters: () => void;
};

const defaultDateRange = getDefaultParrillaDateRange();

const DEFAULT_PAGINATION_VALUES: PaginationValues = {
  currentPage: 1,
  offset: 0,
  rowsPerPage: 10,
  query: '',
  sortBy: { id: null, order: null },
};

export const useParrillaFilters = create<ParrillaFiltersState>(set => {
  const setSearchQuery = debounce(
    (query: string) =>
      set(state => ({
        paginationValues: resetToFirstPage({ ...state.paginationValues, query }),
      })),
    400
  );

  return {
    order_by: '',
    review_state: [],
    priorities: [],
    gte: defaultDateRange.start.toISOString(),
    lte: defaultDateRange.end.toISOString(),
    channel_id: '',
    channel_media_type: '',
    deliveryDate: [],
    paginationValues: DEFAULT_PAGINATION_VALUES,
    program_source_id__isnull: null,
    program_viewed_place_id: '',
    setFilters: newFilters =>
      set(state => ({
        ...state,
        ...newFilters,
        paginationValues: resetToFirstPage(state.paginationValues),
      })),
    setPage: page =>
      set(state => ({
        paginationValues: {
          ...state.paginationValues,
          currentPage: page,
          offset: (page - 1) * state.paginationValues.rowsPerPage,
        },
      })),
    setRows: rowsPerPage =>
      set(state => ({
        paginationValues: {
          ...state.paginationValues,
          rowsPerPage,
          offset: (state.paginationValues.currentPage - 1) * rowsPerPage,
        },
      })),
    setSearchQuery,
    setSortBy: sortBy =>
      set(state => ({ paginationValues: { ...state.paginationValues, sortBy } })),
    clearFilters: () => {
      // RISK: the search is debounced, so a term typed just before clearing would
      // land afterwards and republish itself over the cleared state.
      setSearchQuery.cancel();

      set(() => {
        // Recomputed here, not the module-level value, so a tab open across
        // midnight clears to the current day.
        const dateRange = getDefaultParrillaDateRange();

        return {
          order_by: '',
          review_state: [],
          priorities: [],
          gte: dateRange.start.toISOString(),
          lte: dateRange.end.toISOString(),
          channel_id: '',
          deliveryDate: [],
          channel_media_type: '',
          paginationValues: DEFAULT_PAGINATION_VALUES,
          program_source_id__isnull: null,
          program_viewed_place_id: '',
        };
      });
    },
  };
});
